import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const normalizeScoutProfile = scout => {
  const firstName = scout?.firstName || scout?.first_name || '';
  const lastName = scout?.lastName || scout?.last_name || '';
  const name = scout?.name || `${firstName} ${lastName}`.trim() || scout?.email || 'Scout';
  return {
    ...scout,
    firstName,
    lastName,
    name,
    stats: scout?.stats || {},
    affiliatedClubs: Array.isArray(scout?.affiliatedClubs) ? scout.affiliatedClubs : [],
    payouts: Array.isArray(scout?.payouts) ? scout.payouts : [],
    playerRequests: Array.isArray(scout?.playerRequests) ? scout.playerRequests : [],
    pushedPlayers: Array.isArray(scout?.pushedPlayers) ? scout.pushedPlayers : [],
    shortlistedPlayers: Array.isArray(scout?.shortlistedPlayers) ? scout.shortlistedPlayers : [],
    recentRatings: Array.isArray(scout?.recentRatings) ? scout.recentRatings : [],
  };
};

const readApiData = async res => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.message || `Request failed with ${res.status}`);
  }
  return body.data?.data || body.data || body;
};

const StatBox = ({ label, value, color }) => (
  <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
    <div style={{ fontSize:22, fontWeight:700, color: color||'#A7F432' }}>{value ?? '—'}</div>
    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{label}</div>
  </div>
);

function ScoutModal({ id, token, onClose, onDelete, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      setLoading(true);
      setData(null);
      try {
        let profile;
        try {
          profile = await readApiData(await fetch(`${API}/scouts/${id}/profile`, { headers: { Authorization: `Bearer ${token}` } }));
        } catch (profileError) {
          console.warn('Scout extended profile failed, falling back to basic profile:', profileError.message);
          profile = await readApiData(await fetch(`${API}/scouts/${id}`, { headers: { Authorization: `Bearer ${token}` } }));
        }
        if (!cancelled) setData(normalizeScoutProfile(profile));
      } catch (e) {
        if (!cancelled) {
          setData(null);
          toast?.(e.message || 'Failed to load scout', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, [id, token, toast]);

  const toggleSuspend = async () => {
    if (!data) return;
    setSuspending(true);
    try {
      const res = await fetch(`${API}/scouts/${id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !data.isActive }),
      });
      const d = await res.json();
      if (d.success) { toast?.(`Scout ${data.isActive ? 'suspended' : 'activated'}`); setData(prev => ({ ...prev, isActive: !prev.isActive })); }
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSuspending(false); }
  };

  const deleteScout = async () => {
    if (!data) return;
    if (!window.confirm(`Permanently delete ${data.name}? This cannot be undone.`)) return;
    setSuspending(true);
    try {
      const res = await fetch(`${API}/scouts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      if (d.success !== false) { toast?.('Scout deleted'); onDelete?.(); onClose(); }
      else toast?.(d.message || 'Delete failed', 'error');
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSuspending(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'24px 16px', overflowY:'auto' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:760, border:'1px solid rgba(255,255,255,0.07)', marginBottom:32 }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          {loading ? <div style={{ color:'var(--text-dim)', fontSize:13 }}>Loading...</div> : data ? (
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:'50%', background:'linear-gradient(135deg,#A7F432,#5a9e1a)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#000', flexShrink:0 }}>
                {data.pfp ? <img src={data.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(data.name)}
              </div>
              <div>
                <div style={{ fontSize:17, fontWeight:700, color:'#fff' }}>{data.name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{data.email} · {data.role}</div>
              </div>
            </div>
          ) : <div style={{ color:'#ff6b6b' }}>Failed to load</div>}
          <div style={{ display:'flex', gap:8 }}>
            {data && <button onClick={toggleSuspend} disabled={suspending} style={{ background: data.isActive ? 'rgba(255,80,80,0.12)' : 'rgba(167,244,50,0.12)', color: data.isActive ? '#ff6b6b' : '#A7F432', border:`1px solid ${data.isActive ? 'rgba(255,80,80,0.3)' : 'rgba(167,244,50,0.3)'}`, borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>{suspending ? '...' : data.isActive ? 'Suspend' : 'Activate'}</button>}
            {data && <button onClick={deleteScout} disabled={suspending} style={{ background:'rgba(255,80,80,0.08)', color:'#ff6b6b', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>Delete</button>}
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 12px', fontSize:13, cursor:'pointer' }}>x</button>
          </div>
        </div>
        {loading && <div style={{ padding:60, textAlign:'center', color:'rgba(255,255,255,0.3)' }}>Loading profile...</div>}
        {!loading && data && (
          <>
            <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              <StatBox label="Evaluations Done" value={data.stats?.evaluationsDone} />
              <StatBox label="Players Pushed" value={data.stats?.playersPushed} />
              <StatBox label="Placements" value={data.stats?.placements} color="#4B9EF5" />
              <StatBox label="Bookmarked Players" value={data.stats?.bookmarkedPlayers} color="#F5A623" />
              <StatBox label="Requests Posted" value={data.stats?.requestsPosted} />
              <StatBox label="Shortlisted" value={data.stats?.shortlistedPlayers} color="#9B59B6" />
            </div>
            {data.balance !== null && data.balance !== undefined && (
              <div style={{ margin:'0 24px 20px', background:'rgba(167,244,50,0.06)', border:'1px solid rgba(167,244,50,0.15)', borderRadius:10, padding:'14px 18px', display:'flex', gap:24, flexWrap:'wrap' }}>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Wallet Balance</div><div style={{ fontSize:18, fontWeight:700, color:'#A7F432' }}>{data.currency||'USD'} {parseFloat(data.balance||0).toFixed(2)}</div></div>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Total Earned</div><div style={{ fontSize:16, fontWeight:600, color:'#fff' }}>{data.currency||'USD'} {parseFloat(data.totalEarned||0).toFixed(2)}</div></div>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Total Paid Out</div><div style={{ fontSize:16, fontWeight:600, color:'#fff' }}>{data.currency||'USD'} {parseFloat(data.totalPaidOut||0).toFixed(2)}</div></div>
              </div>
            )}
            <div style={{ padding:'0 24px', display:'flex', gap:4, borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
              {['overview','affiliations','ratings','payouts'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 16px', fontSize:12, fontWeight:600, textTransform:'capitalize', color: tab===t ? '#A7F432' : 'rgba(255,255,255,0.4)', borderBottom: tab===t ? '2px solid #A7F432' : '2px solid transparent' }}>{t}</button>
              ))}
            </div>
            <div style={{ padding:'20px 24px 24px' }}>
              {tab === 'overview' && (
                <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden' }}>
                  {[['Phone',data.phoneNumber],['Nationality',data.nationality],['Area',data.area],['Primary Language',data.primaryLanguage],['License Status',data.licenseVerificationStatus],['Status',data.isActive?'Active':'Suspended'],['Joined',fmtDate(data.createdAt)]].map(([label,value],i,arr) => (
                    <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom: i<arr.length-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                      <div style={{ width:160, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                      <div style={{ fontSize:13, color: value?'#fff':'rgba(255,255,255,0.25)' }}>{value||'—'}</div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'affiliations' && (
                <div>{data.affiliatedClubs?.length ? data.affiliatedClubs.map(c => (
                  <div key={c.clubId} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', marginBottom:10 }}>
                    <div style={{ fontWeight:600, color:'#fff', fontSize:14 }}>{c.clubName}</div>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{c.clubEmail} · {c.country}</div>
                    <div style={{ fontSize:11, color:'#A7F432', marginTop:4 }}>Joined {fmtDate(c.createdAt)}</div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No affiliated clubs</div>}</div>
              )}
              {tab === 'ratings' && (
                <div>{data.recentRatings?.length ? data.recentRatings.map(r => (
                  <div key={r.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#A7F432,#5a9e1a)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#000', flexShrink:0 }}>{initials(`${r.firstName||''} ${r.lastName||''}`)}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{r.firstName} {r.lastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{r.position} · {fmtDate(r.createdAt)}</div>
                    </div>
                    <div style={{ fontSize:12, color: r.recommendTrial ? '#A7F432' : 'rgba(255,255,255,0.3)' }}>{r.recommendTrial ? 'Trial Recommended' : 'No trial'}</div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#F5A623' }}>{r.overallImpression}/10</div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No ratings submitted yet</div>}</div>
              )}
              {tab === 'payouts' && (
                <div>{data.payouts?.length ? data.payouts.map(p => (
                  <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div><div style={{ fontSize:14, fontWeight:600, color:'#fff' }}>{p.currency} {parseFloat(p.amount).toFixed(2)}</div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{fmtDate(p.createdAt)}</div></div>
                    <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background: p.status==='completed'?'rgba(167,244,50,0.12)':p.status==='pending'?'rgba(245,166,35,0.12)':'rgba(255,80,80,0.12)', color: p.status==='completed'?'#A7F432':p.status==='pending'?'#F5A623':'#ff6b6b' }}>{p.status}</span>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No payout requests</div>}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Scouts({ toast }) {
  const { token } = useApp();
  const [scouts, setScouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (p=1, s='') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/scouts?page=${p}&limit=20${s?`&search=${encodeURIComponent(s)}`:''}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setScouts(d.data||[]); setPagination(d.pagination||null);
    } catch(e) { toast?.(e.message,'error'); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(1,''); }, [load]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div><h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Scouts</h1><p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>All registered scouts on the platform</p></div>
        {pagination && <div style={{ fontSize:12, color:'var(--text-dim)', background:'var(--bg-card)', padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)' }}>{pagination.total} scouts</div>}
      </div>
      <form className="toolbar" onSubmit={e => { e.preventDefault(); setPage(1); load(1,search); }} style={{ marginBottom:16 }}>
        <input className="search-input" placeholder="Search by name, email..." value={search} onChange={e=>setSearch(e.target.value)} />
        <button type="submit" className="btn btn-ghost">Search</button>
        {search && <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); load(1,''); }}>Clear</button>}
      </form>
      <div className="card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span className="card-title">All Scouts</span>
          {pagination && <span style={{ fontSize:12, color:'var(--text-dim)' }}>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>}
        </div>
        {loading ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading scouts...</div>
        : scouts.length === 0 ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No scouts found</div>
        : (
          <div className="table-wrap"><table>
            <thead><tr><th>Scout</th><th>Nationality</th><th>License</th><th>Status</th><th>Joined</th><th></th></tr></thead>
            <tbody>{scouts.map(s => {
              const name = s.name || `${s.firstName||''} ${s.lastName||''}`.trim();
              return (
                <tr key={s.id}>
                  <td><div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#A7F432,#5a9e1a)', fontSize:11, fontWeight:700, color:'#000' }}>{s.pfp ? <img src={s.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(name)}</div>
                    <div><div style={{ fontWeight:500, fontSize:13 }}>{name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{s.email}</div></div>
                  </div></td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{s.nationality||'—'}</td>
                  <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background: s.licenseVerificationStatus==='Verified'?'rgba(167,244,50,0.12)':'rgba(255,165,0,0.12)', color: s.licenseVerificationStatus==='Verified'?'#A7F432':'#FFA500' }}>{s.licenseVerificationStatus||'Unknown'}</span></td>
                  <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background: s.isActive?'rgba(167,244,50,0.12)':'rgba(255,80,80,0.12)', color: s.isActive?'#A7F432':'#ff6b6b' }}>{s.isActive?'Active':'Suspended'}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{fmtDate(s.createdAt)}</td>
                  <td><button className="btn btn-ghost" style={{ fontSize:11, padding:'5px 12px' }} onClick={() => setSelectedId(s.id)}>View</button></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={() => { const np=page-1; setPage(np); load(np,search); }}>Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=pagination.totalPages} onClick={() => { const np=page+1; setPage(np); load(np,search); }}>Next</button>
        </div>
      )}
      {selectedId && <ScoutModal id={selectedId} token={token} onClose={() => setSelectedId(null)} onDelete={() => load(page, search)} toast={toast} />}
    </div>
  );
}
